// src/App.tsx
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom"; // Import Router, Routes, Route, and useLocation

// Components that are always present (e.g., Navbar, Footer, Preloader)
import Navbar from "./components/Navbar/Navbar";
import Preloader from "./components/Preloader/Preloader";
import Footer from "./components/Footer/Footer";

// Import all your section components for the main page
import HomeHero from "./sections/HomeHero/HomeHero";
import ITSolutions from "./sections/Services/ITSolutions/ITSolutions"; // Note: changed ID to it-services in comments
import DigitalMarketing from "./sections/Services/DigitalMarketing/DigitalMarketing";
import AboutUs from "./sections/Services/AboutUs/AboutUs";
import WhyChooseUs from "./sections/Services/WhyChooseUs/WhyChooseUs";
import HowWeWork from "./sections/Services/HowWeWork/HowWeWork";
import StatsSection from "./sections/Stats/StatsSection";
import TestimonialCarousel from "./components/Testimonials/TestimonialCarousel"; // Changed path here based on previous structure
import ContactUs from "./sections/Services/ContactUs/ContactUs"; // Assuming ContactUs is also a homepage section

// Import your dedicated page components
import ServicesPage from "./pages/Services"; // Your Services page
import ContactPage from "./pages/Contact"; // Your Services page
import PrivacyPolicyPage from "./pages/PrivavcyPolicy"; // Your Privacy Policy page
import TermsAndConditionsPage from "./pages/TermsAndConditions"; // Your Terms and Conditions page

// Import your global styles (where Tailwind CSS is imported)
import "./styles/index.css";

// Component to handle scrolling to hash on the homepage
const HomeSections: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Check if there's a hash in the URL and we are on the root path
    if (location.pathname === "/" && location.hash) {
      const element = document.getElementById(location.hash.substring(1)); // Remove '#'
      if (element) {
        // Use setTimeout to ensure the DOM has rendered before trying to scroll
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100); // Small delay, adjust if needed
      }
    } else if (location.pathname === "/" && !location.hash) {
      // If navigating to home without a hash, scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location]); // Re-run effect when location changes

  return (
    <main>
      <HomeHero id="home-hero" />
      {/* If you add a "Feature" section, uncomment and provide an ID */}
      {/* <FeatureSection id="features" /> */}
      <ITSolutions id="it-solutions" />{" "}
      {/* Make sure this ID matches your Navbar link */}
      <DigitalMarketing id="digital-marketing" />
      <AboutUs id="aboutus" />
      <WhyChooseUs id="why-choose-us" />
      <HowWeWork id="how-we-work" />
      <StatsSection />
      <TestimonialCarousel id="testimonials" />
    </main>
  );
};

const App: React.FC = () => {
  return (
    <div className="font-sans antialiased text-gray-900">
      <Preloader />
      <Navbar /> {/* Navbar is always visible, outside of Routes */}
      <Routes>
        {/* Route for the Home Page, which includes all your sections */}
        <Route path="/" element={<HomeSections />} />
        {/* Routes for your other distinct pages */}
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/contactUs" element={<ContactPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route
          path="/terms-and-conditions"
          element={<TermsAndConditionsPage />}
        />{" "}
        *{/* Optional: A catch-all route for 404 Not Found pages */}
        <Route
          path="*"
          element={
            <div className="flex flex-col items-center justify-center min-h-[calc(100vh-150px)] text-center">
              <h1 className="text-5xl font-bold text-gray-800 mb-4">404</h1>
              <p className="text-2xl text-gray-600">Page Not Found</p>
              <p className="text-lg text-gray-500 mt-2">
                The page you're looking for does not exist.
              </p>
              <a href="/" className="mt-6 text-blue-600 hover:underline">
                Go to Home
              </a>
            </div>
          }
        />
      </Routes>
      <Footer /> {/* Footer is always visible, outside of Routes */}
    </div>
  );
};

export default App;
